<script>
import { mapActions } from "vuex";
import InfiniteAutocomplete from "./InfiniteAutocomplete.vue";

/**
 * 収運・処分事業者・事業場選択コンポーネント
 *
 */
export default {
  name: "InfiniteTraderAndLocationAutoComplete",
  components: {
    InfiniteAutocomplete,
  },
  props: {
    /* 事業者ID */
    traderId: {
      type: Number,
      default: null
    },
    /* 事業場ID */
    locationId: {
      type: Number,
      default: null,
    },
    locationCode: {
      type: String,
      default: null,
    },
    traderLabel: {
      type: String,
      default: "収運・処分業者",
    },
    locationLabel: {
      type: String,
      default: "事業場",
    },
    traderRequired: {
      type: Boolean,
      default: false,
    },
    locationRequired: {
      type: Boolean,
      default: false,
    },
    traderDisabled: {
      type: Boolean,
      default: false,
    },
    locationDisabled: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    classes: {
      type: String,
      default: null,
    },
    /**
     * カラム幅の指定
     */
    traderMd: {
      type: [Number, String],
      default: 12,
    },
    locationMd: {
      type: [Number, String],
      default: 12,
    },
    /**
     * 展開する種別
     * transport: 運搬業者
     * trader: 処分業者
     * either: 運搬・処分両方
     */
    searchType: {
      type: String,
      default: "transport",
      validator(val) {
        return ["transport", "trader", "either"].includes(val);
      },
    },
    /**

     *
     */
    withoutInvisible: {
      type: Boolean,
      default: false,
    },
    excludeMe: {
      type: Boolean,
      default: false,
    },
    withoutNoneReporting: {
      type: Boolean,
      default: () => false,
    },
    withoutNoneSubscribe: {
      type: Boolean,
      default: () => false,
    },
    includeGeneralWasteDisposer: {
      type: Boolean,
      default: () => false,
    },
    /**
     * 積替公共種別が積替で登録された事業場のみ
     */
    onlyTransship: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザー操作で事業者を選択した際に事業場が1件であれば
     * 自動選択する.
     */
    locationAutoSelect: {
      type: Boolean,
      default: false
    },
    withNoneLocation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      traderLoading: false,
      traderSearchKeyword: null,
      traderTimerId: undefined,
      traderPageNumber: 0,
      traderPagination: {
        next_page: null,
        prev_page: null,
        is_first_page: false,
        is_last_page: false,
      },
      locationLoading: false,
      locationSearchKeyword: null,
      locationTimerId: undefined,
      locationPageNumber: 0,
      locationPagination: {
        next_page: null,
        prev_page: null,
        is_first_page: false,
        is_last_page: false,
      },
      traders: [],
      locations: [],
      workableId: null,
      workableLocationId: null,
      workableLocationCode: null,
      changedByOperate: false,
    };
  },
  computed: {
    /**
     * 事業場の自動選択を行うかどうか。
     * 呼び出し元にて「locationAutoSelect」にtrueが指定され、ユーザーによりドロップダウンが変更された場合
     * @return {false|boolean}
     */
    shouldSelectFirstLocation () {
      return this.locationAutoSelect && this.changedByOperate;
    },
  },
  watch: {
    /**
     * 事業者IDの変更検知
     * @param newValue 事業者ID
     * @param oldValue
     */
    traderId(newValue, oldValue) {
      this.traderSearchKeyword = null;
      this.locationSearchKeyword = null;
      this.workableId = newValue;
      if (newValue !== oldValue) {
        this.loadTrader(this.workableId);
        this.reloadLocations();
      }
    },
    /**
     * 事業場IDの変更検知
     * @param value
     */
    locationId(value) {
      this.traderSearchKeyword = null;
      this.locationSearchKeyword = null;
      this.workableLocationId = value;
    },
    /**
     * 事業場コードの変更検知
     * @param newValue
     */
    locationCode(newValue) {
      this.workableLocationCode = newValue
    },
    /**
     * 作業用排出事業場IDの変更検知
     */
    workableId() {
      this.returnCurrentValues();
    },
    /**
     * 作業用排出事業者IDの変更検知
     */
    workableLocationId() {
      this.returnCurrentValues();
    },
  },
  /**
   * 画面ロード時に呼ばれる.
   * ここで初期設定などを行う.
   */
  created() {
    if (this.traderId) {
      this.workableId = this.traderId;
      this.loadTrader(this.workableId);
    } else {
      this.fetchTraderMembers();
    }

    if (this.locationCode) {
      this.workableLocationCode = this.locationCode;
    }

    if (this.locationId) {
      if (this.traderId) {
        this.workableLocationId = this.locationId;
        this.reloadLocations();
      } else {
        this.workableLocationId = this.locationId;
        this.fetchLocationMembers();
      }
    } else {
      this.fetchLocationMembers();
    }
  },
  methods: {
    ...mapActions("DriverMasterSearchTraderMember", {
      fetchTraders: "index",
    }),
    ...mapActions("DriverMasterSearchTraderLocationMember", {
      fetchTraderLocations: "index",
    }),
    /**
     * 事業者抽出条件の共通部を定義
     * @return {{search_type: string, exclude_me: boolean, without_non_reporting: boolean, without_non_subscribe: boolean, include_general_waste_disposer: boolean, only_transship: boolean}}
     */
    sharedCondition () {
      return {
        search_type: this.searchType,
        exclude_me: this.excludeMe,
        without_non_reporting: this.withoutNoneReporting,
        without_non_subscribe: this.withoutNoneSubscribe,
        without_invisible: this.withoutInvisible,
        include_general_waste_disposer: this.includeGeneralWasteDisposer,
        only_transship: this.onlyTransship,
        with_none_location: this.withNoneLocation
      }
    },
    /**
     * 事業者のロード
     * @param id { Number } 事業者ID
     */
    loadTrader(id) {
      const conditions = {
        ...this.sharedCondition(),
        target_id: id,
        page: 1,
      };
      this.fetchTraders({ params: conditions }).then((response) => {
        this.traders = response.traders;
        this.traderPagination = response.pagination;
        this.traderPageNumber = response.pagination.current_page;
      });
    },
    /**
     * 事業者 リロード処理
     */
    reloadTraders() {
      this.traderSearchKeyword = null;
      this.traderPageNumber = 0;
      this.traders = [];
      Object.assign(
        this.$data.traderPagination,
        this.$options.data().traderPagination
      );
      this.fetchTraderMembers();
    },
    /**
     * 事業場 リロード処理
     */
    reloadLocations() {
      this.locationSearchKeyword = null;
      this.locationPageNumber = 0;
      this.locations = [];
      Object.assign(
        this.$data.locationPagination,
        this.$options.data().locationPagination
      );
      this.fetchLocationMembers();
    },
    /**
     * 排出事業者オプション取得アクション
     * @returns {Promise<unknown>}
     */
    fetchTraderMembers() {
      this.traderLoading = true;
      const conditions = {
        ...this.sharedCondition(),
        query: this.traderSearchKeyword,
        location_id: this.workableLocationId,
        page: this.traderPageNumber,
      };

      this.fetchTraders({ params: conditions }).then((response) => {
        this.traders = this.traders.concat(response.traders);
        // 排出事業場が選択済で事業者リストが1件の場合は選択状態とする.
        if (this.workableLocationId && this.traders.length === 1) {
          this.workableId = this.traders[0].value;
        }
        this.traderPagination = response.pagination;
        this.traderPageNumber = response.pagination.current_page;
      })
        .finally(() => {
          this.traderLoading = false;
        });
    },
    /**
     * 排出事業場オプション取得アクション
     */
    fetchLocationMembers() {
      this.locationLoading = true;
      const conditions = {
        ...this.sharedCondition(),
        query: this.locationSearchKeyword,
        trader_id: this.workableId,
      };
      this.fetchTraderLocations({ query: conditions, page: this.locationPageNumber }).then((response) => {
        this.locations = this.locations.concat(response.locations);
        if (this.shouldSelectFirstLocation && this.workableId && this.locations.length === 1) {
          this.workableLocationId = this.locations[0].value;
          this.workableLocationCode = this.locations[0].code;
        }
        this.locationPagination = response.pagination;
        this.locationPageNumber = response.pagination.current_page;
      })
      .finally(() => {
        this.locationLoading = false;
        this.changedByOperate = false;
      });
    },
    /**
     * 事業者チェンジ コールバック
     * ・事業場の再取得を行う
     */
    traderChangeCallback() {
      this.workableLocationCode = null;
      this.workableLocationId = null;
      this.changedByOperate = true;
      this.reloadLocations();
    },
    /**
     * 事業場チェンジ コールバック
     * ・事業者の再取得を行う
     */
    locationChangeCallback() {
      const object = { id: this.workableLocationId, location: null };
      if (this.workableLocationId) {
        object["location"] = this.locations.find( (v) => v.value === this.workableLocationId );
      }

      this.workableLocationCode = null;
      if (object["location"]) {
        this.workableLocationCode = object["location"]["code"];
      }

      if (object && object["location"]["parent_id"] !== this.workableId) {
        this.workableId = object["location"]["parent_id"];
        this.reloadTraders();
      }
    },
    /**
     * 事業者クリア コールバック
     */
    traderClearCallback() {
      this.workableLocationCode = null;
      this.workableLocationId = null;
      this.workableId = null;
      this.reloadTraders();
    },
    /**
     * 事業場クリア コールバック
     */
    locationClearCallback() {
      this.workableLocationCode = null;
      this.workableLocationId = null;
      this.reloadLocations();
    },
    /**
     * 排出事業者
     * 次ページ読込
     */
    traderInsectCallback() {
      if (this.traderPageNumber > 0 && this.traders.length > 0) {
        this.traderPageNumber++;
      }
      this.fetchTraderMembers();
    },
    /**
     * 排出事業場
     * 次ページ読込
     */
    locationInsectCallback() {
      if (this.locationPageNumber > 0) {
        this.locationPageNumber++;
        this.fetchLocationMembers();
      }
    },
    traderEnterKeydownCallback (value) {
      this.traderPageNumber = 0;
      this.traderSearchKeyword = value;
      this.fetchTraderMembers();
    },
    locationEnterKeydownCallback (value) {
      this.locationPageNumber = 0;
      this.locationSearchKeyword = value;
      this.fetchLocationMembers();
    },
    returnCurrentValues () {
      this.$emit("update:values", this.workableId, this.workableLocationId, this.workableLocationCode)
    },
  },
};
</script>
<template>
  <div>
    <v-row>
      <v-col cols="12" :md="traderMd">
        <validation-provider
          v-slot="{ errors }"
          :class="{ requiredMark: traderRequired }"
          :name="traderLabel"
          :rules="{ required: traderRequired }"
          :value="workableId"
        >
          <InfiniteAutocomplete
            v-model="workableId"
            :classes="classes"
            :disabled="traderDisabled"
            :errorMessages="errors"
            :label="traderLabel"
            :loading="traderLoading"
            :members="traders"
            :pagination="traderPagination"
            :rounded="rounded"
            item-text="label"
            @change="traderChangeCallback"
            @click:clear="traderClearCallback"
            @insect="traderInsectCallback"
            @keydown:enter="traderEnterKeydownCallback"
          >
            <template v-slot:prepend>
              <slot name="trader-prepend" />
            </template>
            <template v-slot:prepend-inner>
              <slot name="trader-prepend-inner" />
            </template>
            <template v-slot:selection="{ item }">
              <slot name="trader-selection" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:item="{ item }">
              <slot name="trader-item" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:append>
              <slot name="trader-append" />
            </template>
            <template v-slot:append-outer>
              <slot name="trader-append-outer" />
            </template>
          </InfiniteAutocomplete>
        </validation-provider>
      </v-col>
      <slot name="between" />
      <v-col cols="12" :md="locationMd">
        <validation-provider
          v-slot="{ errors }"
          :class="{ requiredMark: locationRequired }"
          :name="locationLabel"
          :rules="{ required: locationRequired }"
          :value="workableLocationId"
        >
          <InfiniteAutocomplete
            v-model="workableLocationId"
            :classes="classes"
            :disabled="locationDisabled"
            :errorMessages="errors"
            :label="locationLabel"
            :loading="locationLoading"
            :members="locations"
            :pagination="locationPagination"
            :rounded="rounded"
            item-text="label"
            @change="locationChangeCallback"
            @click:clear="locationClearCallback"
            @insect="locationInsectCallback"
            @keydown:enter="locationEnterKeydownCallback"
          >
            <template v-slot:prepend>
              <slot name="location-prepend" />
            </template>
            <template v-slot:prepend-inner>
              <slot name="location-prepend-inner" />
            </template>
            <template v-slot:selection="{ item }">
              <slot name="location-selection" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:item="{ item }">
              <slot name="location-item" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:append>
              <slot name="location-append" />
            </template>
            <template v-slot:append-outer>
              <slot name="location-append-outer" />
            </template>
          </InfiniteAutocomplete>
        </validation-provider>
      </v-col>
      <slot name="append"></slot>
    </v-row>
  </div>
</template>
